import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/context/AuthContext";

const STATUSES = [
  { value: "in_progress", label: "In progress" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
];

export default function InstructorTrainingOutcome({ learnerId, programmeId }) {
  const [outcome, setOutcome] = useState(null);
  const [form, setForm] = useState({
    training_status: "in_progress",
    licence_stage: "",
    achievement_date: "",
    instructor_confirmed_outcome: "",
    learner_reported_outcome: "",
    completion_notes: "",
    next_steps: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!learnerId) return Promise.resolve();
    return api
      .get("/training-outcomes/instructor", {
        params: { learner_id: learnerId, programme_id: programmeId || "programme-nz-road-code" },
      })
      .then((res) => {
        const o = res.data?.outcome;
        setOutcome(o);
        if (o) {
          setForm({
            training_status: o.training_status || "in_progress",
            licence_stage: o.licence_stage || "",
            achievement_date: o.achievement_date || "",
            instructor_confirmed_outcome: o.instructor_confirmed_outcome || "",
            learner_reported_outcome: o.learner_reported_outcome || "",
            completion_notes: o.completion_notes || "",
            next_steps: o.next_steps || "",
          });
        }
      })
      .catch((err) => setError(formatApiError(err)));
  }, [learnerId, programmeId]);

  useEffect(() => {
    load();
  }, [load]);

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.put("/training-outcomes/instructor", {
        learner_id: learnerId,
        programme_id: programmeId || "programme-nz-road-code",
        recording_source: "instructor_confirmed",
        ...form,
      });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!learnerId) return null;

  return (
    <section className="card space-y-2" data-testid="instructor-training-outcome">
      <h3 className="font-semibold text-sm">Training completion & licence outcome</h3>
      <p className="text-xs text-muted-foreground">
        Record training status and licence stage separately — not inferred from quizzes or hours.
      </p>
      <form className="space-y-2" onSubmit={save}>
        <label className="text-xs block">
          Training status
          <select
            className="input mt-1"
            value={form.training_status}
            onChange={(e) => setForm((f) => ({ ...f, training_status: e.target.value }))}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs block">
          Licence stage achieved
          <input
            className="input mt-1"
            value={form.licence_stage}
            onChange={(e) => setForm((f) => ({ ...f, licence_stage: e.target.value }))}
          />
        </label>
        <label className="text-xs block">
          Achievement date
          <input
            className="input mt-1"
            type="date"
            value={form.achievement_date}
            onChange={(e) => setForm((f) => ({ ...f, achievement_date: e.target.value }))}
          />
        </label>
        <label className="text-xs block">
          Instructor-confirmed outcome
          <textarea
            className="input mt-1 min-h-[60px]"
            value={form.instructor_confirmed_outcome}
            onChange={(e) => setForm((f) => ({ ...f, instructor_confirmed_outcome: e.target.value }))}
          />
        </label>
        <label className="text-xs block">
          Learner-reported outcome (on file)
          <textarea
            className="input mt-1 min-h-[60px]"
            value={form.learner_reported_outcome}
            onChange={(e) => setForm((f) => ({ ...f, learner_reported_outcome: e.target.value }))}
          />
        </label>
        <label className="text-xs block">
          Completion notes
          <textarea
            className="input mt-1 min-h-[60px]"
            value={form.completion_notes}
            onChange={(e) => setForm((f) => ({ ...f, completion_notes: e.target.value }))}
          />
        </label>
        <label className="text-xs block">
          Next steps
          <textarea
            className="input mt-1 min-h-[60px]"
            value={form.next_steps}
            onChange={(e) => setForm((f) => ({ ...f, next_steps: e.target.value }))}
          />
        </label>
        <button type="submit" className="btn-outline" disabled={busy}>Save outcome record</button>
      </form>
      {outcome?.updated_at && (
        <p className="text-xs text-muted-foreground">Last updated: {outcome.updated_at}</p>
      )}
      {error && <p className="text-destructive text-sm">{error}</p>}
    </section>
  );
}
